import "./WorkflowPermissions.css";

import { Dispatch, SetStateAction } from "react";

import { WorkflowOrganisationAccessLevelsSelectData } from "../../../../../../../../../constants";
import { ProjectDatesData } from "../../../../../../../../../models";
import { ServiceError } from "../../../../../../../../../service/Shared";
import {
  ActionButtons,
  Column,
  Divider,
  ErrorBox,
  InformationBox,
  Row,
  Select,
} from "../../../../../../../../../widget";
import { useWorkflowPermissions } from "./useWorkflowPermissions";

interface WorkflowPermissionsProps {
  workflowTemplateUuid: string | undefined;
  selectedProject: ProjectDatesData | undefined;
  errors: ServiceError[] | undefined;
  setErrors: Dispatch<SetStateAction<ServiceError[] | undefined>>;
  onClose: () => void;
}

export const WorkflowPermissions = ({
  workflowTemplateUuid,
  selectedProject,
  errors,
  setErrors,
  onClose,
}: WorkflowPermissionsProps): JSX.Element => {
  const {
    workflowOrganisations,
    isApplyWorkflowTemplateLoading,
    showOwnerOrganisationTasksWarning,
    handleUpdateOrganisationAccessLevel,
    handleApplyWorkflowTemplate,
    handleClose,
  } = useWorkflowPermissions({
    workflowTemplateUuid,
    selectedProject,
    onClose,
    setErrors,
  });
  return (
    <div className="WorkflowPermissions">
      <Row spacingV="xl">
        <Column span={12}>
          <Divider type="horizontal" />
        </Column>
      </Row>
      <Row spacingV="xl">
        <Column span={12}>
          <h2>Workflow permissions</h2>
        </Column>
      </Row>
      {errors && errors.length > 0 && (
        <Row spacingV="ml">
          <Column span={6}>
            <ErrorBox text={errors.map((error) => error.message)} />
          </Column>
        </Row>
      )}
      <Row spacingV={showOwnerOrganisationTasksWarning ? "ml" : "xl"}>
        <Column span={12}>
          <InformationBox title="What can each role do?" variant="info">
            <ul>
              <li>
                Owner: Full access to create, update, and delete all elements of the workflow. Owners can also edit the
                access levels for other organisations.
              </li>
              <li>
                Author: Full access to create, update, and delete all elements of the workflow, but without permission
                to edit organisational access levels.
              </li>
              <li>
                Contributor: Ability to add and edit steps and tasks in the workflow, as well as delete all elements of
                the workflow they have created.
              </li>
              <li>
                User: Ability to progress tasks they own, but without the ability to add or remove elements of the
                workflow.
              </li>
              <li>Viewer: Read-only access to the workflow.</li>
            </ul>
          </InformationBox>
        </Column>
      </Row>
      {showOwnerOrganisationTasksWarning && (
        <Row spacingV="xl">
          <Column span={12}>
            <InformationBox
              title="One or more tasks in this template have owners that aren't being granted access to this workflow."
              variant="alert"
            >
              <p className="body2">
                If you proceed without granting access, those tasks will not be visible to that organisation.
              </p>
            </InformationBox>
          </Column>
        </Row>
      )}
      <form onSubmit={(e) => handleApplyWorkflowTemplate(e)}>
        <Row spacingV="xl">
          <Column span={12}>
            <Row spacingV="m" align="center">
              <Column span={4}>
                <p className="body3">
                  <b>Organsiation</b>
                </p>
              </Column>
              <Column span={4}>
                <p className="body3">
                  <b>Organsiation role</b>
                </p>
              </Column>
              <Column span={4}>
                <p className="body3">
                  <b>Access level</b>
                </p>
              </Column>
            </Row>
            <Row>
              <Column span={12}>
                <Divider type="horizontal" />
              </Column>
            </Row>
            {workflowOrganisations.map((org, index) => (
              <div className="WorkflowOrganisationRow" key={org.uuid}>
                <Row key={org.uuid} align="center">
                  <Column span={4}>
                    <p className="body2">{org.displayName}</p>
                  </Column>
                  <Column span={4}>
                    <p className="body2">{org.role}</p>
                  </Column>
                  <Column span={3}>
                    {index === 0 ? (
                      <p className="body2">{org.accessLevel}</p>
                    ) : (
                      <Select
                        data={WorkflowOrganisationAccessLevelsSelectData}
                        onChange={(value) => handleUpdateOrganisationAccessLevel(org.uuid, value)}
                        hasBorder={false}
                        fontSize="body2"
                      />
                    )}
                  </Column>
                </Row>
              </div>
            ))}
          </Column>
        </Row>
        <ActionButtons onCancel={handleClose} isLoading={isApplyWorkflowTemplateLoading} />
      </form>
    </div>
  );
};
